// Gradient pairs for chart bars [base/dark, top/light]
export const chartGradients: [string, string][] = [
  ["#5B93D3", "#93C5FD"], // soft blue
  ["#6ABF69", "#A7E9A0"], // soft green
  ["#E08080", "#F4AEAE"], // soft rose
  ["#E0A84C", "#F5D89A"], // soft amber
  ["#9B7FC4", "#C8B6E2"], // soft purple
  ["#5AADAD", "#96D6D6"], // soft teal
  ["#D98E6A", "#F0C4A8"], // soft peach
  ["#7094B8", "#A8C8E0"], // steel blue
  ["#8AAF6A", "#BDD8A0"], // soft olive
  ["#B87CA0", "#DDB0CC"], // soft mauve
];

// Flat base colors (for legends, tooltips, markers)
export const chartColors = chartGradients.map(([base]) => base);

// Build ECharts linear gradient object (bottom→top)
export const getBarGradient = (index: number) => {
  const [base, light] = chartGradients[index % chartGradients.length];
  return {
    type: "linear" as const,
    x: 0,
    y: 1,
    x2: 0,
    y2: 0,
    colorStops: [
      { offset: 0, color: base },
      { offset: 1, color: light },
    ],
  };
};

// Horizontal bar gradient (left→right)
export const getHBarGradient = (index: number) => {
  const [base, light] = chartGradients[index % chartGradients.length];
  return {
    type: "linear" as const,
    x: 0,
    y: 0,
    x2: 1,
    y2: 0,
    colorStops: [
      { offset: 0, color: base },
      { offset: 1, color: light },
    ],
  };
};

// Avatar/card colors — soft pastel backgrounds
export const colors = [
  "#5B93D3",
  "#6ABF69",
  "#E08080",
  "#E0A84C",
  "#9B7FC4",
  "#5AADAD",
  "#D98E6A",
  "#7094B8",
  "#8AAF6A",
  "#B87CA0",
];

export const getSupplierInitials = (name: string) => {
  const words = name?.split(" ");
  return words?.length > 1
    ? `${words[0][0]}${words[1][0]}`.toUpperCase()
    : name?.substring(0, 2).toUpperCase();
};

export const formatMYR = (value: number) =>
  value.toLocaleString("en-IN", { minimumFractionDigits: 2 });

export interface MaterialInfo {
  tenderMaterialId: number;
  materialName: string;
  usage?: string;
  unitName?: string;
  floorNo?: string;
  subUnitName?: string;
  locationName?: string;
  needByDate?: string;
}

const findLineItem = (lineItems: any[], mat: MaterialInfo) =>
  lineItems.find((li: any) => li.tenderMaterialId === mat.tenderMaterialId);

export const calculateLowestPrices = (
  materials: MaterialInfo[],
  suppliers: any[]
): Record<number, number> => {
  const lowestPricePerMaterial: Record<number, number> = {};
  materials.forEach((mat) => {
    let minPrice = Infinity;
    suppliers.forEach((s) => {
      const lineItem = findLineItem(s.lineItems || [], mat);
      if (lineItem) {
        const total = lineItem.quotedQuantity * lineItem.quotedUnitPrice + lineItem.totalInstallationCharges - lineItem.discountAmount;
        if (total < minPrice) minPrice = total;
      }
    });
    lowestPricePerMaterial[mat.tenderMaterialId] = minPrice;
  });
  return lowestPricePerMaterial;
};

export const calculateSummaryData = (
  suppliers: any[],
  materials: MaterialInfo[]
) => {
  return suppliers.map((supplier) => {
    const items = supplier.lineItems || [];
    let subTotal = 0;
    let totalInstallationCharges = 0;
    let lineDiscountTotal = 0;
    materials.forEach((mat) => {
      const lineItem = findLineItem(items, mat);
      if (lineItem) {
        subTotal += lineItem.quotedQuantity * lineItem.quotedUnitPrice;
        totalInstallationCharges += lineItem.totalInstallationCharges || 0;
        lineDiscountTotal += lineItem.discountAmount || 0;
      }
    });
    // Additional discount: use fixed amount from supplier, proportioned by filtered share
    // const additionalDiscountAmt = supplier?.additionalDiscountAmount || 0;
    // const additionalDiscountPct = supplier.additionalDiscount || 0;
    // let proportionedAdditionalDiscount = additionalDiscountAmt;
    // if (additionalDiscountAmt > 0) {
    //   // Calculate full subtotal (all line items) to determine proportion
    //   let fullSubTotal = 0;
    //   let fullInstallation = 0;
    //   let fullLineDiscount = 0;
    //   (items || []).forEach((li: any) => {
    //     fullSubTotal += (li.quotedQuantity || 0) * (li.quotedUnitPrice || 0);
    //     fullInstallation += li.totalInstallationCharges || 0;
    //     fullLineDiscount += li.discountAmount || 0;
    //   });
    //   const fullBase = fullSubTotal + fullInstallation - fullLineDiscount;
    //   const filteredBase = subTotal + totalInstallationCharges - lineDiscountTotal;
    //   const proportion = fullBase > 0 ? filteredBase / fullBase : 1;
    //   proportionedAdditionalDiscount = additionalDiscountAmt * proportion;
    // }
    const totalDiscount = lineDiscountTotal;
    const taxableAmount = subTotal + totalInstallationCharges - totalDiscount;
    const taxRate = items[0]?.taxPercentage || 6;
    const taxAmount = taxableAmount * (taxRate / 100);
    const grossAmount = taxableAmount + taxAmount;
    const effectiveDiscountPct = (subTotal + totalInstallationCharges) > 0
      ? (totalDiscount / (subTotal + totalInstallationCharges)) * 100
      : 0;
    return {
      supplierID: supplier.supplierID,
      subTotal,
      totalInstallationCharges,
      taxableAmount,
      taxes: taxAmount,
      taxRate,
      grandTotal: grossAmount,
      grossAmount,
      paymentTerms: supplier.paymentTerms,
      totalDiscount,
      discountPercentage: parseFloat(effectiveDiscountPct.toFixed(3)),
      displayDiscount : `${formatMYR(totalDiscount)}  (${effectiveDiscountPct.toFixed(3)}%)`
    };
  });
};

export const buildMaterialDataSource = (
  materials: MaterialInfo[],
  suppliers: any[],
  shortlistedData: any[]
) => {
  return materials.map((mat, idx) => {
    const row: any = {
      key: String(idx),
      materialName: mat.materialName,
      tenderMaterialId: mat.tenderMaterialId,
      unitName: mat.unitName || "",
      floorNo: mat.floorNo || "",
      subUnitName: mat.subUnitName || "",
    };

    const firstSupplier = suppliers.find((s) =>
      (s.lineItems || []).some((li: any) => li.tenderMaterialId === mat.tenderMaterialId)
    );
    const firstLineItem = findLineItem(firstSupplier?.lineItems || [], mat);
    row.askedQty = firstLineItem?.askedQuantity || 0;
    row.uom = firstLineItem?.uom || "-";
    row.usage = firstLineItem?.usage || "-";
    row.needByDate = mat.needByDate || firstLineItem?.needByDate || "";

    const awardedForMaterial = shortlistedData
      .filter((s) => s.tenderMaterialId === mat.tenderMaterialId)
      .reduce((sum, s) => sum + s.awardQty, 0);
    row.awardedQty = awardedForMaterial;
    row.remainingQty = Math.max(row.askedQty - awardedForMaterial, 0);

    suppliers.forEach((s) => {
      const lineItem = findLineItem(s.lineItems || [], mat);
      row[s.supplierID] = lineItem
        ? {
            supplyQty: lineItem.quotedQuantity,
            unitPrice: lineItem.quotedUnitPrice,
            totalInstallationCharges: lineItem.totalInstallationCharges || 0,
            totalPrice:
              lineItem.quotedQuantity * lineItem.quotedUnitPrice +
              (lineItem.totalInstallationCharges || 0) -
              (lineItem.discountAmount || 0),
            taxPercentage: lineItem.taxPercentage,
            discountAmount: lineItem.discountAmount || 0,
            discountPercent: lineItem.discountPercent || lineItem.discountPercentage || 0,
          }
        : null;
    });

    return row;
  });
};

export const getSupplierTaxableTotal = (
  supplier: any,
  materials: MaterialInfo[]
) => {
  const items = supplier.lineItems || [];
  let subTotal = 0;
  let totalInstallationCharges = 0;
  let lineDiscountTotal = 0;
  materials.forEach((mat) => {
    const lineItem = findLineItem(items, mat);
    if (lineItem) {
      subTotal += lineItem.quotedQuantity * lineItem.quotedUnitPrice;
      totalInstallationCharges += lineItem.totalInstallationCharges || 0;
      lineDiscountTotal += lineItem.discountAmount || 0;
    }
  });
  const totalDiscount = (supplier?.additionalDiscountAmount || 0) + lineDiscountTotal;
  return subTotal + totalInstallationCharges - totalDiscount;
};

export const getSupplierGrandTotal = (
  supplier: any,
  materials: MaterialInfo[]
) => {
  const subTotal = getSupplierTaxableTotal(supplier, materials);
  const items = supplier.lineItems || [];
  const taxRate = items[0]?.taxPercentage || 6;
  return subTotal + subTotal * (taxRate / 100);
};
